import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ListShape } from '../../owner-game-lists-api';
import * as S from './ListLayoutPicker.styles';

/** The layouts in the order the cards offer them, and the fewest games each is meant for: the plain grid
 *  fills a row with three, the row (a carousel) a screen with eight, and the mixed grid lays out twenty-four. */
export const LIST_LAYOUTS: readonly ListShape[] = ['grid', 'row', 'mixed'];
const LAYOUT_MIN: Record<ListShape, number> = { grid: 3, row: 8, mixed: 24 };

/** Each layout drawn as what it puts on the page: thumbnails in one row, large thumbnails in a grid, and small
 *  ones with two large among them. Outlined like the hub's own icons. */
const glyph = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true } as const;
const outline = { stroke: 'currentColor', strokeWidth: 1.7, strokeLinejoin: 'round' } as const;

const RowGlyph = (): React.ReactElement => (
  <svg {...glyph}>
    <rect x="1.5" y="6" width="6" height="12" rx="1.6" {...outline} />
    <rect x="9" y="6" width="6" height="12" rx="1.6" {...outline} />
    <rect x="16.5" y="6" width="6" height="12" rx="1.6" {...outline} />
  </svg>
);

const GridGlyph = (): React.ReactElement => (
  <svg {...glyph}>
    <rect x="3" y="3" width="8" height="8" rx="1.8" {...outline} />
    <rect x="13" y="3" width="8" height="8" rx="1.8" {...outline} />
    <rect x="3" y="13" width="8" height="8" rx="1.8" {...outline} />
    <rect x="13" y="13" width="8" height="8" rx="1.8" {...outline} />
  </svg>
);

/** The small thumbnails of the mixed grid's glyph, around its two large ones. */
const MIXED_SMALL = [
  [13, 3],
  [17.5, 3],
  [13, 7.5],
  [17.5, 7.5],
  [3, 13],
  [7.5, 13],
  [3, 17.5],
  [7.5, 17.5],
] as const;

const MixedGridGlyph = (): React.ReactElement => (
  <svg {...glyph}>
    <rect x="2.5" y="2.5" width="9" height="9" rx="1.8" {...outline} />
    <rect x="12.5" y="12.5" width="9" height="9" rx="1.8" {...outline} />
    {MIXED_SMALL.map(([x, y]) => (
      <rect key={`${x}-${y}`} x={x} y={y} width="3.5" height="3.5" rx="0.8" fill="currentColor" />
    ))}
  </svg>
);

const LayoutGlyph = ({ layout }: { layout: ListShape }): React.ReactElement =>
  layout === 'row' ? <RowGlyph /> : layout === 'mixed' ? <MixedGridGlyph /> : <GridGlyph />;

export type ListLayoutPickerProps = {
  value: ListShape;
  onChange: (layout: ListShape) => void;
};

/**
 * The layout of one of the owner's game lists, as three cards: each the layout drawn, its name and the fewest
 * games it is meant for, the chosen one in the call-to-action colours. Three across, stacked on a phone.
 *
 * A native radio group: each card is the label of its radio, which is out of sight, so a click anywhere on a card
 * picks it and the arrow keys move the choice with no code of their own. A radio is named by its layout alone and
 * described by its minimum.
 */
export const ListLayoutPicker = ({ value, onChange }: ListLayoutPickerProps): React.ReactElement => {
  const { t } = useTranslation();
  const id = React.useId();

  const name = (layout: ListShape): string =>
    // Literal t() per name, so the extractor harvests each.
    layout === 'row' ? t('Row') : layout === 'mixed' ? t('Mixed grid') : t('Grid');
  const min = (layout: ListShape): string => t('min. {{n}}', { n: LAYOUT_MIN[layout] });

  return (
    <S.Cards role="radiogroup" aria-label={t('List layout')}>
      {LIST_LAYOUTS.map((layout) => {
        const on = layout === value;
        return (
          <S.Card key={layout} $on={on}>
            <S.Radio
              type="radio"
              name="list-layout"
              value={layout}
              checked={on}
              onChange={() => onChange(layout)}
              aria-labelledby={`${id}-${layout}-name`}
              aria-describedby={`${id}-${layout}-min`}
            />
            <LayoutGlyph layout={layout} />
            <S.Name id={`${id}-${layout}-name`}>{name(layout)}</S.Name>
            <S.Min id={`${id}-${layout}-min`}>{min(layout)}</S.Min>
          </S.Card>
        );
      })}
    </S.Cards>
  );
};

export default ListLayoutPicker;
